import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { auth } from '../firebase';

/**
 * Get traffic source from referrer, UTM parameters, or session storage
 * @returns {string} Traffic source (e.g., 'google', 'instagram', 'direct', etc.)
 */
function getTrafficSource() {
  // Check UTM parameters first
  const urlParams = new URLSearchParams(window.location.search);
  const utmSource = urlParams.get('utm_source');
  const utmMedium = urlParams.get('utm_medium');
  const utmCampaign = urlParams.get('utm_campaign');

  if (utmSource) {
    // Store in sessionStorage for subsequent page views
    sessionStorage.setItem('traffic_source', utmSource);
    sessionStorage.setItem('traffic_medium', utmMedium || '');
    sessionStorage.setItem('traffic_campaign', utmCampaign || '');
    return utmSource.toLowerCase();
  }

  // Check sessionStorage for previously stored source
  const storedSource = sessionStorage.getItem('traffic_source');
  if (storedSource) {
    return storedSource.toLowerCase();
  }

  // Check document referrer
  const referrer = document.referrer;
  if (!referrer) {
    return 'direct';
  }

  try {
    const referrerUrl = new URL(referrer);
    const hostname = referrerUrl.hostname.toLowerCase();

    // Common sources
    if (hostname.includes('google') || hostname.includes('google.com')) {
      return 'google';
    }
    if (hostname.includes('facebook') || hostname.includes('fb.com')) {
      return 'facebook';
    }
    if (hostname.includes('instagram') || hostname.includes('instagram.com')) {
      return 'instagram';
    }
    if (hostname.includes('tiktok') || hostname.includes('tiktok.com')) {
      return 'tiktok';
    }
    if (hostname.includes('twitter') || hostname.includes('x.com')) {
      return 'twitter';
    }
    if (hostname.includes('linkedin')) {
      return 'linkedin';
    }
    if (hostname.includes('youtube')) {
      return 'youtube';
    }
    if (hostname.includes('whatsapp')) {
      return 'whatsapp';
    }

    // Store the referrer domain as source
    const domain = hostname.replace('www.', '');
    sessionStorage.setItem('traffic_source', domain);
    return domain;
  } catch (error) {
    return 'unknown';
  }
}

/**
 * Log an analytics event to Firestore
 * @param {string} eventType - Type of event (e.g., 'page_view', 'cta_click', 'favorite_added')
 * @param {string} page - Current page path (e.g., '/', '/search', '/hairstylist/123')
 * @param {object} metadata - Additional metadata (e.g., { button: 'search', hairstylistId: '123' })
 */
export async function logEvent(eventType, page, metadata = {}) {
  try {
    const user = auth.currentUser;
    const userId = user?.uid || null;

    // Ne pas comptabiliser les actions du compte admin dans les stats globales
    const displayName = user?.displayName || user?.email?.split('@')[0] || '';
    if (displayName.toLowerCase() === 'admin') {
      return;
    }
    const trafficSource = getTrafficSource();

    await addDoc(collection(db, 'pageEvents'), {
      userId,
      eventType,
      page: page || window.location.pathname,
      metadata: {
        ...metadata,
        source: trafficSource,
      },
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    // Silently fail - analytics should not break the app
    // eslint-disable-next-line no-console
    console.error('Failed to log analytics event:', error);
  }
}

/**
 * Log a page view event
 * @param {string} page - Page path
 */
export function logPageView(page) {
  return logEvent('page_view', page);
}

/**
 * Log a CTA button click
 * @param {string} page - Current page
 * @param {string} buttonName - Name/ID of the button clicked
 * @param {object} additionalMetadata - Additional metadata
 */
export function logButtonClick(page, buttonName, additionalMetadata = {}) {
  return logEvent('cta_click', page, {
    button: buttonName,
    ...additionalMetadata,
  });
}

/**
 * Log a favorite action
 * @param {string} page - Current page
 * @param {string} hairstylistId - ID of the hairstylist
 * @param {boolean} isAdded - true if added, false if removed
 */
export function logFavoriteAction(page, hairstylistId, isAdded) {
  return logEvent(isAdded ? 'favorite_added' : 'favorite_removed', page, {
    hairstylistId,
  });
}

/**
 * Log a contact click
 * @param {string} page - Current page
 * @param {string} hairstylistId - ID of the hairstylist
 * @param {string} contactType - Type of contact (e.g., 'instagram', 'whatsapp', 'tiktok')
 */
export function logContactClick(page, hairstylistId, contactType) {
  return logEvent('contact_clicked', page, {
    hairstylistId,
    contactType,
  });
}

/**
 * Log signup events
 * @param {string} page - Current page
 * @param {string} step - Signup step (e.g., 'started', 'completed')
 */
export function logSignupEvent(page, step) {
  const eventType = step === 'started' ? 'signup_started' : 'signup_completed';
  return logEvent(eventType, page);
}

/**
 * Log payment events
 * @param {string} page - Current page
 * @param {string} status - Payment status ('started', 'success', 'failed')
 */
export function logPaymentEvent(page, status) {
  const eventType = `payment_${status}`;
  return logEvent(eventType, page);
}

